import type { HttpClientAdapter } from "../types"

function readBearerToken(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem("bearer")
}

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown
  ) {
    super(`HTTP ${status}: ${statusText}`)
    this.name = "HttpError"
  }
}

/**
 * Hard cap on a single request. Without it a request that black-holes (TCP
 * stalls, dead proxy, sleeping mobile) hangs forever — and a hung delta GET
 * pins the SyncEngine's `resyncing` flag, which silently drops every live SSE
 * event for that resource until reload. The abort surfaces as `AbortError`,
 * which the engine already treats as a transient/retryable failure and which
 * releases the resync flag via its `finally`.
 */
const DEFAULT_TIMEOUT_MS = 30_000

export class FetchHttpClient implements HttpClientAdapter {
  constructor(
    private baseUrl: string,
    private getAuthToken?: () => string | null,
    private timeoutMs: number = DEFAULT_TIMEOUT_MS
  ) {}

  private authHeaders(): Record<string, string> {
    // Try explicit getter first, then read bearer token from localStorage
    const token = this.getAuthToken?.() ?? readBearerToken()
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  /**
   * Run a request and parse its JSON under ONE abort timer. The timer is held
   * across the whole operation — connect, response head, AND body read — and
   * only cleared once the body is fully consumed (or the request throws), so a
   * stall at any stage trips the abort. A response that black-holes (or whose
   * body stream stalls mid-parse) surfaces as `AbortError`, which the engine
   * treats as transient and which releases the resync flag via its `finally`.
   */
  private async requestJson<T>(input: string, init: RequestInit): Promise<T> {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), this.timeoutMs)
    try {
      const response = await fetch(input, {
        ...init,
        signal: controller.signal,
      })
      if (!response.ok) {
        const body = await response.text().catch(() => null)
        throw new HttpError(response.status, response.statusText, body)
      }
      return (await response.json()) as T
    } finally {
      clearTimeout(timer)
    }
  }

  async get<T>(url: string, params?: Record<string, string>): Promise<T> {
    let fullUrl = `${this.baseUrl}${url}`
    if (params) {
      const searchParams = new URLSearchParams(params)
      fullUrl += (fullUrl.includes("?") ? "&" : "?") + searchParams.toString()
    }
    return this.requestJson<T>(fullUrl, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json", ...this.authHeaders() },
    })
  }

  async post<T>(url: string, body: unknown): Promise<T> {
    return this.requestJson<T>(`${this.baseUrl}${url}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...this.authHeaders(),
      },
      body: JSON.stringify(body),
    })
  }

  async delete<T>(url: string): Promise<T> {
    return this.requestJson<T>(`${this.baseUrl}${url}`, {
      method: "DELETE",
      credentials: "include",
      headers: { Accept: "application/json", ...this.authHeaders() },
    })
  }
}
